// src/components/legal/PrawoSpolekMA.js

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, ArrowRight, ChevronDown, ChevronUp, BriefcaseBusiness , FilePenLine, Building2, Layers, Briefcase, FileText, Search, Handshake, ChartArea  } from 'lucide-react';
import Trust from './Trust';
import ContactForm from './ContactForm';

// Add font import
const fontLink = document.createElement('link');
fontLink.href = 'https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap';
fontLink.rel = 'stylesheet';
document.head.appendChild(fontLink);

const PrawoSpolekMA = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const corporateServices = [
    { icon: <Building2 className="w-8 h-8" />, title: "Zakładanie i rejestracja spółek", description: "Pomoc w wyborze formy prawnej, przygotowanie umów spółek, rejestracja w KRS." },
    { icon: <Briefcase className="w-8 h-8" />, title: "Bieżąca obsługa korporacyjna", description: "Obsługa posiedzeń zarządu, rad nadzorczych, zgromadzeń wspólników, sporządzanie uchwał, dokumentacji wewnętrznej." },
    { icon: <Layers className="w-8 h-8" />, title: "Restrukturyzacja i reorganizacja", description: "Doradztwo w zakresie przekształceń, podziałów, łączenia spółek." },
    { icon: <FileText className="w-8 h-8" />, title: "Umowy handlowe", description: "Przygotowywanie i negocjowanie umów sprzedaży, dostawy, dystrybucji, agencyjnych." },
  ];

  const maServices = [
      { icon: <Search className="w-8 h-8" />, title: "Due diligence", description: "Kompleksowa analiza prawna przejmowanego podmiotu." },
      { icon: <Layers className="w-8 h-8" />, title: "Strukturyzowanie transakcji", description: "Doradztwo w wyborze optymalnej formy transakcji (zakup udziałów, aktywów, aport)." },
      { icon: <FileText className="w-8 h-8" />, title: "Negocjacje i przygotowanie umów", description: "Sporządzanie i negocjowanie umów inwestycyjnych, przedwstępnych, końcowych." },
      { icon: <Handshake className="w-8 h-8" />, title: "Wsparcie po transakcji", description: "Pomoc w integracji prawno-organizacyjnej przejętych podmiotów." },
  ];

  const faqs = [
    {
      question: "Jakie są kluczowe etapy transakcji M&A?",
      answer: "Kluczowe etapy to: due diligence (analiza prawna, finansowa, podatkowa), negocjacje warunków transakcji, przygotowanie i podpisanie dokumentacji transakcyjnej (np. list intencyjny, umowa przedwstępna, umowa sprzedaży), a także wsparcie po transakcji w zakresie integracji."
    },
    {
      question: "Czy pomagacie w wyborze odpowiedniej formy prawnej dla nowej firmy?",
      answer: "Tak, doradzamy w wyborze optymalnej formy prawnej działalności (np. jednoosobowa działalność gospodarcza, spółka cywilna, spółka z o.o., spółka akcyjna), uwzględniając specyfikę biznesu, plany rozwoju i kwestie odpowiedzialności."
    },
    {
      question: "Co to jest due diligence prawne w transakcjach M&A?",
      answer: "Due diligence prawne to szczegółowa analiza stanu prawnego spółki lub przedsiębiorstwa, które ma być przedmiotem transakcji. Ma na celu identyfikację potencjalnych ryzyk prawnych (np. spory, zadłużenie, brak ważnych umów) i ocenę ich wpływu na wartość transakcji."
    }
  ];

  const handleContactScroll = () => {
    const contactSection = document.querySelector('#kontakt');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-black text-white pt-20">
      {/* Hero Section */}
      <section className="py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-20 animate-float">
            <ChartArea  className="w-32 h-32 text-violet-600" />
          </div>
          <div className="absolute bottom-20 left-20 animate-float animation-delay-2000">
            <BriefcaseBusiness  className="w-24 h-24 text-violet-500" />
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6 leading-tight uppercase" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 900 }}>
              Prawo Spółek i M&A
            </h1>
            <p className="text-xl lg:text-2xl text-white mb-8 font-semibold max-w-4xl mx-auto">
              Kompleksowe Wsparcie Prawne dla Twojego Biznesu
            </p>
            <p className="text-lg text-gray-300 mb-12 max-w-3xl mx-auto">
              Od założenia, przez rozwój, restrukturyzację, aż po fuzje i przejęcia – Szynkowski Legal oferuje pełne wsparcie prawne dla przedsiębiorstw. Z nami Twój biznes zyska solidne fundamenty i bezpiecznie pokona wszelkie wyzwania korporacyjne.
            </p>
            <div className="flex justify-center">
              <button 
                onClick={handleContactScroll}
                className="group flex items-center gap-3 text-white text-xl lg:text-2xl font-medium hover:text-violet-500 transition-all duration-300 uppercase"
              >
                <span>Bezpłatna konsultacja</span>
                <ArrowRight className="w-6 h-6 text-violet-600 group-hover:text-violet-500 group-hover:translate-x-2 group-hover:scale-110 transition-all duration-300" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-white mb-6 md:mb-8 uppercase leading-tight" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 900 }}>
              Wsparcie prawne na każdym etapie życia spółki
            </h2>
            <p className="text-lg text-gray-300">
              Prawo spółek i transakcje M&A to obszary wymagające nie tylko dogłębnej wiedzy, ale także strategicznego myślenia. Nasz zespół prawników posiada bogate doświadczenie w doradztwie korporacyjnym, wspierając klientów w codziennym funkcjonowaniu, jak i w kluczowych momentach, takich jak fuzje, przejęcia czy restrukturyzacje.
            </p>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-5xl font-black text-white mb-4 uppercase leading-tight" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 900 }}>
              Nasze usługi w zakresie prawa spółek
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {corporateServices.map((service, index) => (
              <div key={index} className="group backdrop-blur-md bg-white/5 rounded-xl p-8 border border-white/10 hover:border-violet-600/50 transition-all duration-300 hover:shadow-xl hover:shadow-violet-600/10">
                <div className="w-16 h-16 bg-gradient-to-br from-violet-600/20 to-violet-700/20 rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <span className="text-violet-600">{service.icon}</span>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{service.title}</h3>
                <p className="text-gray-400">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* M&A Services Section */}
        <section className="py-16 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-violet-600/5 to-transparent"></div>
            <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
                <div className="text-center mb-12">
                    <h2 className="text-2xl sm:text-3xl lg:text-5xl font-black text-white mb-4 uppercase leading-tight" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 900 }}>
                    Fuzje i Przejęcia (M&A)
                    </h2>
                    <p className="text-lg text-gray-300 max-w-3xl mx-auto">Profesjonalne doradztwo na każdym etapie transakcji</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {maServices.map((service, index) => (
                    <div key={index} className="group backdrop-blur-md bg-white/5 rounded-xl p-8 border border-white/10 hover:border-violet-600/50 transition-all duration-300 hover:shadow-xl hover:shadow-violet-600/10">
                        <div className="w-16 h-16 bg-gradient-to-br from-violet-600/20 to-violet-700/20 rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                        <span className="text-violet-600">{service.icon}</span>
                        </div>
                        <h3 className="text-xl font-bold text-white mb-3">{service.title}</h3>
                        <p className="text-gray-400">{service.description}</p>
                    </div>
                    ))}
                </div>
                 <div className="mt-12 text-center">
                   <p className="text-xl text-gray-300 mb-8">
                     Zabezpiecz przyszłość swojego biznesu. Skontaktuj się z nami po profesjonalne doradztwo w prawie spółek i M&A!
                   </p>
                   <button 
                     onClick={handleContactScroll}
                     className="group flex items-center gap-3 text-white text-xl lg:text-2xl font-medium hover:text-violet-500 transition-all duration-300 uppercase mx-auto"
                   >
                     <span>Bezpłatna konsultacja</span>
                     <ArrowRight className="w-6 h-6 text-violet-600 group-hover:text-violet-500 group-hover:translate-x-2 group-hover:scale-110 transition-all duration-300" />
                   </button>
                 </div>
            </div>
        </section>

      {/* FAQ Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <h2 className="text-3xl lg:text-5xl font-black text-white mb-12 text-center uppercase" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 900 }}>
            FAQ: Prawo Spółek i M&A
          </h2>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="backdrop-blur-md bg-white/5 rounded-xl border border-white/10 overflow-hidden transition-all duration-300 hover:border-violet-600/30">
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between group"
                >
                  <h3 className="text-lg font-semibold text-white pr-4">{faq.question}</h3>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-violet-600 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400 group-hover:text-violet-600 transition-colors flex-shrink-0" />
                  )}
                </button>
                <div className={`transition-all duration-300 ${openFaq === index ? 'max-h-96' : 'max-h-0'} overflow-hidden`}>
                  <div className="px-6 pb-4">
                    <p className="text-gray-400">{faq.answer}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Trust />
      <ContactForm />

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-30px) rotate(10deg); }
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        .animation-delay-2000 { animation-delay: 2s; }
      `}</style>
    </div>
  );
};

export default PrawoSpolekMA;